<template>
  <info-panel
    v-if="scenario"
    :item="scenario"
    :title="scenario?.title"
    :sections="sections"
    :extra-settings="extraSettings"
    @update:sections="updateSections($event)"
    @update:extra-settings="updateExtraSettings($event)"
    @close="emit('close')"
  >
    <!-- SETTINGS -->
    <template #label-showEmptySections>{{
      $t("labels.showEmptySections")
    }}</template>

    <template #label-description>
      {{ $t("labels.description") }}
    </template>
    <template
      #description
      v-if="settingsValues.showEmptySections || !isEmpty(scenario.description)"
    >
      <formatted-read-more-text
        :value="scenario.description"
        :placeholder="$t('errors.noDescription')"
      />
    </template>

    <template #label-startscreen>
      {{ $t("labels.startScreen") }}
    </template>
    <template
      #startscreen
      v-if="
        settingsValues.showEmptySections ||
        startScreenImageData ||
        scenario.startTitle ||
        scenario.startText
      "
    >
      <div class="info-panel__imagetext">
        <img
          v-if="startScreenImageData"
          :src="startScreenImageData"
          class="info-panel__description-image"
        />
        <div>
          <h2 v-if="scenario.startTitle" class="subtitle">
            {{ scenario.startTitle }}
          </h2>
          <formatted-text
            :value="scenario.startText"
            :placeholder="$t('errors.noStartText')"
          />
        </div>
      </div>
    </template>

    <template #label-endscreen>
      {{ $t("labels.endScreen") }}
    </template>
    <template
      #endscreen
      v-if="
        settingsValues.showEmptySections ||
        endScreenImageData ||
        scenario.endTitle ||
        scenario.endText
      "
    >
      <div class="info-panel__imagetext">
        <img
          v-if="endScreenImageData"
          :src="endScreenImageData"
          class="info-panel__description-image"
        />
        <div>
          <h2 v-if="scenario.endTitle" class="subtitle">
            {{ scenario.endTitle }}
          </h2>
          <formatted-text
            :value="scenario.endText"
            :placeholder="$t('errors.noEndText')"
          />
        </div>
      </div>
    </template>

    <!-- ACTIONS -->
    <template #label-actions>
      {{ $t("objectNames.action", 2) }}
    </template>
    <template
      #actions
      v-if="settingsValues.showEmptySections || actions.length > 0"
    >
      <div class="line--wrap" v-if="actions.length > 0">
        <button-item
          v-for="action of actions"
          :key="'action' + action.id"
          @click="openOverlay(action)"
        >
          <span v-if="action.title">{{ action.title }}</span>
          <span v-else class="placeholder-text">
            {{ $t("labels.unnamed") }}
          </span>
        </button-item>
      </div>
      <p v-else class="message mv-0">
        {{ $t("texts.noActionsAssigned") }}
      </p>
    </template>

    <!-- INCIDENTS -->
    <template #label-incidents>
      {{ $t("objectNames.incident", 2) }}
    </template>
    <template
      #incidents
      v-if="settingsValues.showEmptySections || incidents.length > 0"
    >
      <div class="line--wrap" v-if="incidents.length > 0">
        <button-item
          v-for="incident of incidents"
          :key="'incident' + incident.id"
          @click="openOverlay(incident)"
        >
          <span v-if="incident.title">{{ incident.title }}</span>
          <span v-else class="placeholder-text">
            {{ $t("labels.unnamed") }}
          </span>
        </button-item>
      </div>
      <p v-else class="message mv-0">
        {{ $t("texts.noEventsAssigned") }}
      </p>
    </template>

    <!-- START VALUES -->
    <template #label-initialValues>
      {{ $t("words.startValue", 2) }}
    </template>
    <template #initialValues>
      <template v-if="orderedTargets.length > 0">
        <!-- targets -->
        <div class="label">
          {{ $t("objectNames.target", 2) }}
        </div>
        <div class="list">
          <template v-if="targetValueCount > 0">
            <div
              class="line_effect"
              v-for="(value, targetId) in scenario.targetValues"
              :key="targetId"
            >
              <button-item
                @click="
                  openOverlay({ type: 'target', id: ensureInt(targetId) })
                "
              >
                <span v-if="targets[ensureInt(targetId)].title">
                  {{ targets[ensureInt(targetId)].title }}
                </span>
                <span v-else class="placeholder-text">
                  {{ $t("labels.unnamed") }}
                </span>
              </button-item>
              <span class="changed-value ta-r">{{ value }}</span>
            </div>
          </template>
          <!-- default values -->
          <template v-if="targetsWithStandardValues.length > 0">
            <div
              class="line_effect"
              v-for="target in targetsWithStandardValues"
              :key="target.id"
            >
              <button-item
                @click="openOverlay({ type: 'target', id: target.id })"
              >
                <span v-if="target.title">{{ target.title }}</span>
                <span v-else class="placeholder-text">
                  {{ $t("labels.unnamed") }}
                </span>
              </button-item>
              <span class="ta-r">{{ target.value }}</span>
            </div>
          </template>
        </div>
      </template>

      <template v-if="orderedResources.length > 0">
        <!-- resources -->
        <div class="label mt-4">
          {{ $t("objectNames.resource", 2) }}
        </div>
        <div class="list">
          <template v-if="resourceValueCount > 0">
            <div
              class="line_effect"
              v-for="(value, resourceId) in scenario.resourceValues"
              :key="resourceId"
            >
              <button-item
                @click="
                  openOverlay({ type: 'resource', id: ensureInt(resourceId) })
                "
              >
                <span v-if="resources[ensureInt(resourceId)].title">
                  {{ resources[ensureInt(resourceId)].title }}
                </span>
                <span v-else class="placeholder-text">
                  {{ $t("labels.unnamed") }}
                </span>
              </button-item>
              <span class="changed-value ta-r">{{ value }}</span>
            </div>
          </template>
          <template v-if="resourcesWithStandardValues.length > 0">
            <div
              class="line_effect"
              v-for="resource in resourcesWithStandardValues"
              :key="resource.id"
            >
              <button-item
                @click="openOverlay({ type: 'resource', id: resource.id })"
              >
                <span v-if="resource.title">{{ resource.title }}</span>
                <span v-else class="placeholder-text">
                  {{ $t("labels.unnamed") }}
                </span>
              </button-item>
              <span class="ta-r">{{ resource.value }}</span>
            </div>
          </template>
        </div>
      </template>
    </template>

    <!-- END CRITERIA -->
    <template #label-ends>
      {{ $t("labels.endCriteria") }}
    </template>
    <template #ends v-if="settingsValues.showEmptySections || ends.length > 0">
      <template v-if="ends.length > 0">
        <div class="column gap-4">
          <div
            v-for="end in ends"
            :key="end.id"
            class="scenario-infopanel__end"
          >
            <h3 class="label mt-0">{{ end.title }}</h3>
            <ul class="mt-1">
              <li
                v-for="condition in endConditions[end.id]"
                :key="condition.id"
              >
                <condition-label :condition-id="condition.id" />
              </li>
            </ul>
            <div
              v-if="end.description"
              class="scenario-infopanel__enddescription"
            >
              <formatted-text :value="end.description" />
            </div>
          </div>
        </div>
      </template>
      <p v-if="ends.length === 0" class="message mv-0">
        {{ $t("texts.noScenarioEnds") }}
      </p>
    </template>
  </info-panel>
</template>

<script setup lang="ts">
import { computed, reactive } from "vue";
import { isEmpty } from "@/helpers/utility/Text";
import { ensureInt } from "@/helpers/utility/Type";
import { mapValues } from "@/helpers/utility/CollectionFunctions";

import ButtonItem from "@core/input/ButtonItem.vue";
import InfoPanel from "@editor/ui/InfoPanel.vue";
import ConditionLabel from "../condition/ConditionLabel.vue";
import FormattedText from "@core/ui/FormattedText.vue";
import FormattedReadMoreText from "@core/ui/FormattedReadMoreText.vue";

import { useTargetStore } from "@editor/items/target/targets.store";
import { useResourceStore } from "@editor/items/resource/resources.store";
import { useActionStore } from "@editor/items/action/actions.store";
import { useInfoPanelStore } from "@editor/store/infoPanels.store";
import { useIncidentStore } from "../incident/incidents.store";
import { useConditionStore } from "../condition/conditions.store";
import { useScenarioEndStore } from "../scenarioEnd/scenarioEnds.store";
import { useImageStore } from "../image/images.store";

import type { InfoPanelSetting } from "@editor/store/infoPanels.store";
import type { ScenarioItem } from "./ScenarioItem";
import type { OverlayDialogItemRequest } from "@editor/ui/OverlayDialogItem";
import type { ConditionItem } from "../condition/ConditionItem";

const props = defineProps<{
  scenario: ScenarioItem | null;
}>();
const emit = defineEmits<{
  (e: "close"): void;
  (e: "select", item: OverlayDialogItemRequest): void;
}>();

const infoPanelStore = useInfoPanelStore();
const conditionStore = useConditionStore();
const imageStore = useImageStore();

const sections = reactive({
  description: true,
  startscreen: true,
  endscreen: true,
  actions: true,
  incidents: true,
  initialValues: true,
  ends: true
});

const extraSettings = reactive({
  showEmptySections: { type: "check" as const, value: true }
});

const actions = computed(() => {
  if (props.scenario === null) return [];
  return useActionStore().getArrayByIds(props.scenario.actionIds);
});
const incidents = computed(() => {
  if (props.scenario === null) return [];
  return useIncidentStore().getArrayByIds(props.scenario.incidentIds);
});
const targets = computed(() => {
  return useTargetStore().items;
});
const orderedTargets = computed(() => {
  return useTargetStore().orderedItems;
});
const resources = computed(() => {
  return useResourceStore().items;
});
const orderedResources = computed(() => {
  return useResourceStore().orderedItems;
});

const startScreenImage = computed(() => {
  if (props.scenario && props.scenario.startImageId != null) {
    return imageStore.items[props.scenario.startImageId];
  }
  return null;
});
const startScreenImageData = computed(() => {
  if (!startScreenImage.value) return undefined;
  return imageStore.mediaData[startScreenImage.value.id] ?? undefined;
});
const endScreenImage = computed(() => {
  if (props.scenario && props.scenario.endImageId != null) {
    return imageStore.items[props.scenario.endImageId];
  }
  return null;
});
const endScreenImageData = computed(() => {
  if (!endScreenImage.value) return undefined;
  return imageStore.mediaData[endScreenImage.value.id] ?? undefined;
});

const changedTargetIds = computed(() => {
  if (props.scenario === null) return [];
  return Object.values(props.scenario.targetValues ?? {}).map(
    (value, targetId) => ensureInt(targetId)
  );
});
const targetsWithStandardValues = computed(() => {
  return orderedTargets.value.filter(
    (target) => !changedTargetIds.value.includes(target.id)
  );
});
const changedResourceIds = computed(() => {
  if (props.scenario === null) return [];
  return Object.values(props.scenario.resourceValues ?? {}).map(
    (value, resourceId) => ensureInt(resourceId)
  );
});
const resourcesWithStandardValues = computed(() => {
  return orderedResources.value.filter(
    (resource) => !changedResourceIds.value.includes(resource.id)
  );
});
const targetValueCount = computed(() => {
  if (props.scenario === null) return 0;
  return Object.keys(props.scenario.targetValues ?? {}).length;
});
const resourceValueCount = computed(() => {
  if (props.scenario === null) return 0;
  return Object.keys(props.scenario.resourceValues ?? {}).length;
});

const ends = computed(() => {
  if (props.scenario === null) return [];
  const ends = useScenarioEndStore().byScenario[props.scenario.id];
  if (ends == null) return [];
  return ends;
});
const endConditions = computed(() => {
  const result: { [endId: number]: ConditionItem[] } = {};

  ends.value.forEach((end) => {
    result[end.id] = conditionStore.orderedItems.filter((c) => {
      return c.srcType === "scenarioEnd" && c.srcId === end.id;
    });
  });

  return result;
});

function openOverlay(item: OverlayDialogItemRequest) {
  emit("select", item);
}

// SECTIONS

function updateSections(value: { [section: string]: boolean }) {
  Object.assign(sections, value);
  infoPanelStore.sections.scenario = sections;
}

function loadSections() {
  // TODO: add sections depending if feature is disabled / enabled
  const savedSections = infoPanelStore.sections?.scenario || {};
  for (const key in sections) {
    if (key in savedSections) {
      sections[key as keyof typeof sections] = savedSections[key];
    }
  }
}

loadSections();

function updateExtraSettings(value: { [setting: string]: InfoPanelSetting }) {
  Object.assign(extraSettings, value);
  infoPanelStore.extraSettings.scenario = extraSettings;
}

function loadExtraSettings() {
  const savedExtraSettings = infoPanelStore.extraSettings?.scenario || {};
  for (const key in extraSettings) {
    if (key in savedExtraSettings) {
      extraSettings[key as keyof typeof extraSettings] = {
        ...(savedExtraSettings[key] as any)
      };
    }
  }
}

const settingsValues = computed(() => {
  return mapValues(extraSettings, (setting) => setting.value);
});

loadExtraSettings();
</script>

<style lang="scss">
.changed-value {
  color: var(--color-action);
}

.scenario-infopanel {
  &__end {
    border-left: 0.5rem solid var(--color-grey);
    padding-left: 1rem;
    padding-block: 0.5rem;
  }
  &__enddescription {
    font-size: 14px;
  }
}
</style>
